import * as React from "react";
import { ReactElement, useEffect, useRef, useState } from 'react';
import {
  Box,
  Stack,
  Heading,
  Text,
  Image,
  Button,
  useColorModeValue,
  Container,
  useToast,
  Flex,
  SimpleGrid,
  StackDivider,
  SpaceProps,
  HStack,
  Tag,
  Link,
  Grid,
  GridItem,
  ButtonGroup,
  Select,
  useDisclosure,
  Spacer
} from "@chakra-ui/react";
import { PageSlideFade } from "../components/page-transitions";
import { apiHost, baseUrl } from "utils/Cons/Constants";
import axios from "axios";
import { Helmet } from "react-helmet";
import headerBg from "../assets/images/header-background.jpg";
import cloudsBg from "../assets/images/cloud-background.png";
import parser from "html-react-parser";
import Masonry from "react-masonry-css";
import "../style/style.css";
import { MotionBox } from "../components/motion";
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L, { Icon, LatLng } from 'leaflet';
import markerIconPng from "../assets/images/marker-icon.png";
import MediaBlock from "../components/media-block";
import FavoritesBlock from "../components/favorites-block";
import ImageBlock from "../components/image-block";
import CrowdBlock from "../components/crowd-block";
import MapBlock from "components/map-block";
import CTABlock from "components/cta-block";
import ImagesBlock from "components/images-block";
import ImagesGridBlock from "components/images-grid-block";
import TabsBlock from "components/tabs-block";
import SlidesBlock from "components/slides-block";
import EmbedBlock from "components/embed-block";
import ContentBlock from "components/content-block";
import BookBlock from "components/book-block";
import FpAdBlock from "components/fp-ad-block";
import FcAdBlock from "components/fc-ad-block";
import FeaturedAdBlock from "components/featured-ad-block";
import HighlightsBlock from "components/highlights-block";
import ShareModal from "components/share-modal";

interface Experience {
  id: number;
  name: string;
  slug: string;
  experience?: string;
  meta_description?: string;
  main_image_url?: string;
  content_blocks?: any[];
  header_image_url?: string;
  content?: string;
  destinations?: Destination[];
}

interface Destination {
  id: number;
  name: string;
  slug: string;
  main_img?: string;
  location_latitude: number;
  location_longitude: number;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  bd_lowest_rate?: number;
  reservations_link?: string;
}

interface SearchValues {
  order?: string;
  categories?: number[];
}

const markdownTheme = {
  p: props => {
    const { children } = props;
    return (
      <Text mb={2} fontSize={'0.8em'}>
        {children}
      </Text>
    );
  },
};



const Experience = ({ match }) => {
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.900');
  const headingColor = useColorModeValue('gray.700', 'white');

  const [destination, setDestination] = useState<Experience>({} as Experience);
  const [loading, setLoading] = useState(true);

  const [grid, setGrid] = useState(true);
  const [breakpointColumns, setBreakpointColumns] = useState({});

  const [searchValues, setSearchValues] = useState<SearchValues>({});
  const [destinations, setDestinations] = useState([]);

  const [selectedDestination, setSelectedDestination] = useState<Destination>({} as Destination);
  const [hoveredDestination, setHoveredDestination] = useState<number | null>(null);

  const initialMarkers = [];
  const [markers, setMarkers] = useState(initialMarkers);

  // Effect to update marker highlighting when hoveredDestination changes from card hover
  useEffect(() => {
    if (hoveredDestination !== null) {
      // Find all marker elements and highlight the one that matches
      const markerElements = document.querySelectorAll('.leaflet-marker-icon');
      markerElements.forEach((element, index) => {
        const htmlElement = element as HTMLElement;
        // Check if this marker corresponds to the hovered destination
        const marker = markers[index];
        if (marker && marker.data.id === hoveredDestination) {
          htmlElement.style.filter = 'brightness(1.2) saturate(1.5)';
          htmlElement.style.transition = 'all 0.2s ease-in-out';
          htmlElement.style.zIndex = '1000';
        } else {
          htmlElement.style.filter = '';
          htmlElement.style.transition = '';
          htmlElement.style.zIndex = '';
        }
      });
    } else {
      // Remove highlighting from all markers
      const markerElements = document.querySelectorAll('.leaflet-marker-icon');
      markerElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.filter = '';
        htmlElement.style.transition = '';
        htmlElement.style.zIndex = '';
      });
    }
  }, [hoveredDestination, markers]);

  const position = [40.9133945, -77.7782867] as [number, number];

  // drawers
  const { isOpen, onOpen, onClose } = useDisclosure()
  const firstField = useRef()

  const {
    isOpen: isFilterOpen,
    onOpen: onFilterOpen,
    onClose: onFilterClose
  } = useDisclosure()

  const breakpointColumnsObj = {
    default: 3,
    1200: 2,
    400: 1
  };

  const breakpointColumnsListObj = {
    default: 1
  };

  const LocationMarkers = () => {
    return (
      <React.Fragment>
        {markers.map(marker => <Marker
          key={marker.data.id}
          position={marker.location}
          icon={new Icon({
            iconUrl: markerIconPng,
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            className: hoveredDestination === marker.data.id ? 'highlighted-marker' : ''
          })}
          eventHandlers={{
            click: (e) => {
              window.location.href = `/d/${marker.data.slug}`;
            },
            mouseover: (e) => {
              setHoveredDestination(marker.data.id);
            },
            mouseout: (e) => {
              setHoveredDestination(null);
            },
          }}>
          <Popup>
            <Link href={`/d/${marker.data.slug}`}>{marker.data.name}</Link><br />
            {marker.data.address}<br />
            {marker.data.city}, {marker.data.state} {marker.data.zip}<br />
            <Link href={`tel:+1${marker.data.phone}`}>{marker.data.phone}</Link>
          </Popup>
        </Marker>)}
      </React.Fragment>
    );
  }

  const getExperience = () => {
    let data = {} as Experience;
    axios.get(`${apiHost}/experiences/get/${match.params.id}`)
      .then(res => {
        data = res.data;
        if (res.status == 200) {
          setDestination(data);

          if (data.destinations) {
            let geomarkers = [];
            data.destinations.forEach(marker => {
              geomarkers.push({ data: marker, location: new LatLng(marker.location_latitude, marker.location_longitude) })
            });
            setMarkers(geomarkers);
          }

          setLoading(false);
        }
        return res;
      })
      .catch(err => console.log(err));

    return data;
  };

  const showToast = (message, status) => {
    toast({
      position: "bottom",
      description: message,
      status: status,
      duration: 5000,
      isClosable: true
    });
  };

  const getDestinations = (values = null) => {
    let data = [];
    axios.post(`${apiHost}/destinations/search`, values)
      .then(res => {
        data = res.data;
        if (res.status == 200) {
          setDestinations(data);

          let geomarkers = [];
          data.forEach(marker => {
            geomarkers.push({ data: marker, location: new LatLng(marker.location_latitude, marker.location_longitude) })
          });
          setMarkers(geomarkers);

          setLoading(false);
        }
        return res;
      })
      .catch(err => console.log(err));

    return data;
  };

  const handleReset = () => {
    const categoryInput = document.getElementById("category") as HTMLInputElement;
    if (categoryInput) categoryInput.value = "";

    const experienceInput = document.getElementById("experience") as HTMLInputElement;
    if (experienceInput) experienceInput.value = "";

    const activityInput = document.getElementById("activity") as HTMLInputElement;
    if (activityInput) activityInput.value = "";
  };

  const handleDestinationClick = (destination) => {
    setSelectedDestination(destination);
    onOpen();
  }

  const sortClick = function (order: string) {
    setSearchValues({ ...searchValues, order });
    getDestinations({ ...searchValues, order });
  }

  const onGridList = function (breakpointColumns) {
    setBreakpointColumns(breakpointColumns)
    if (breakpointColumns.default == 1) {
      setGrid(false);
    } else {
      setGrid(true);
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    getExperience();
  }, []);

  return (
    <PageSlideFade>
      <Helmet>
        <title>{destination.name}</title>
        <meta name="description" content={destination.meta_description} />
      </Helmet>
      {destination.header_image_url != null ? (
        <Box className="home-header-container" backgroundImage={`url(${destination.header_image_url})`} backgroundPosition="center center" backgroundSize="cover" minHeight="60dvh">
          <Container maxW={'5xl'}>

          </Container>
        </Box>
      ) : null}
      {destination.slug != null ? (
        <>
          <Box bg="#143b49">
            <Container maxW={'5xl'}>
              <SimpleGrid columns={1}>
                <Box>
                  <Text
                    as="h1"
                    textAlign="left"
                    fontFamily="american"
                    fontWeight={600}
                    fontSize={{ base: '3xl', sm: '4xl', md: '5xl' }}
                    lineHeight={'120%'}
                    color="#fbeec8"
                    mt={{ base: 4, md: 0 }}
                  >{destination.name}</Text>
                </Box>
              </SimpleGrid>
            </Container>
          </Box>
          <Box bg="#FBEEC8">
            <Container maxW={'6xl'}>
              {destination.content != null ? (
                <Box pt={10} pb={10}>
                  {destination.experience}
                </Box>
              ) : null}
            </Container>

            <Container maxW={'5xl'} pb={10}>
              <Box className="content-block" textAlign="left" bg="white" p={10} roundedBottomRight={20}>
                {destination.content != null ? (
                  <Text textAlign="left">
                    {parser(destination.content)}
                  </Text>
                ) : null}
                <SimpleGrid columns={3} mt={10}>
                  <Box></Box>
                  <Box></Box>
                  <Box>
                    <ShareModal page={destination} path="experiences/" />
                  </Box>
                </SimpleGrid>
              </Box>

            </Container>

          </Box>

          {destination.content_blocks?.map((block, index) => (
            <Box key={`block-${index}`}>
              {
                {
                  'anomaly.extension.favorites_block_block': <FavoritesBlock page={destination} block={block} />,
                  'anomaly.extension.media_block_block': <MediaBlock page={destination} block={block} />,
                  'anomaly.extension.image_block_block': <ImageBlock page={destination} block={block} />,
                  'anomaly.extension.crowd_block_block': <CrowdBlock page={destination} block={block} />,
                  'anomaly.extension.map_block_block': <MapBlock page={destination} block={block} />,
                  'anomaly.extension.cta_block_block': <CTABlock page={destination} block={block} />,
                  'anomaly.extension.images_block_block': <ImagesBlock page={destination} block={block} />,
                  'anomaly.extension.images_grid_block_block': <ImagesGridBlock page={destination} block={block} />,
                  'anomaly.extension.tabs_block_block': <TabsBlock page={destination} block={block} />,
                  'anomaly.extension.slides_block_block': <SlidesBlock page={destination} block={block} />,
                  'anomaly.extension.highlights_block_block': <HighlightsBlock page={destination} block={block} />,
                  'anomaly.extension.embed_block_block': <EmbedBlock page={destination} block={block} />,
                  'anomaly.extension.content_block_block': <ContentBlock page={destination} block={block} />,
                  'anomaly.extension.book_block_block': <BookBlock page={destination} block={block} />,
                  'anomaly.extension.fp_ad_block_block': <FpAdBlock page={destination} block={block} />,
                  'anomaly.extension.fc_ad_block_block': <FcAdBlock page={destination} block={block} />,
                  'anomaly.extension.featured_ad_block_block': <FeaturedAdBlock page={destination} block={block} />,
                }[block.extension]
              }
            </Box>
          ))}

          <Box bg="white">
            <Box pt={10} display="none">
              <SimpleGrid columns={3}>
                <Box textAlign="left">
                  <Button onClick={() => onFilterOpen()} p={5} borderLeftRadius={0}>Filter</Button>
                </Box>
                <Box display={{ base: "none", md: "block" }}>
                  <ButtonGroup spacing='2'>
                    <Button 
                      onClick={() => onGridList(breakpointColumnsObj)} 
                      p={5}
                      borderTopEndRadius={0}
                      borderBottomEndRadius={0}
                      bg={grid ? "#D51F0C" : "gray.200"}
                      color={grid ? "white" : "black"}
                      _hover={{
                        bg: grid ? "#143b49" : "gray.300"
                      }}
                    >
                      Grid
                    </Button>
                    <Button 
                      onClick={() => onGridList(breakpointColumnsListObj)} 
                      p={5}
                      borderTopStartRadius={0}
                      borderBottomStartRadius={0}
                      bg={!grid ? "#D51F0C" : "gray.200"}
                      color={!grid ? "white" : "black"}
                      _hover={{
                        bg: !grid ? "#143b49" : "gray.300"
                      }}
                    >
                      List
                    </Button>
                  </ButtonGroup>
                </Box>
                <Box display="flex" flexDirection="row" alignItems="right" >
                  <HStack marginLeft="auto" mb={5} mr={5}>
                    <Spacer />
                    <Text w="100px">Sort By</Text>
                    <Select
                      onChange={(e) => {
                        sortClick(e.target.value);
                      }} w="200px">
                      <option value="updated" selected>Last Updated</option>
                      <option value="name">Business Name</option>
                    </Select>
                  </HStack>
                </Box>
              </SimpleGrid>
            </Box>
            <SimpleGrid columns={{ base: 1, 'xxl': 2 }} spacing={10}>
              <Box p={5}>

                {loading ? null : (
                  <Masonry breakpointCols={breakpointColumns}
                    className="masonry-grid"
                    columnClassName="masonry-grid_column">

                    {destination.destinations?.map((destination, index) => (
                      <Box
                        w={'100%'}
                        bg={hoveredDestination === destination.id ? '#E6DFD3' : "#FBEEC8"}
                        boxShadow={'0 0 10px 2px #ccc'}
                        rounded={'md'}
                        _hover={{
                          bg: '#E6DFD3'
                        }}
                        display="inline-block"
                        mb={5}
                        className="masonry"
                        onMouseEnter={() => setHoveredDestination(destination.id)}
                        onMouseLeave={() => setHoveredDestination(null)}
                        transition="background-color 0.2s ease-in-out"
                      >
                        <Box>
                          <Box position="relative">
                            {destination.main_img && (
                              <Link href={`/d/${destination.slug}`}>
                                <Image
                                  src={`${destination.main_img}`}
                                  w={'100%'}
                                  rounded={'md'}
                                  borderBottomRadius={"0"}
                                />
                              </Link>
                            )}
                            {destination.bd_lowest_rate && (
                              <Link
                                href={destination.reservations_link}
                                target="_blank"
                                fontWeight={'bold'}
                                fontSize="15px"
                                color="white"
                                whiteSpace={'nowrap'}
                                bg="#D51F0C"
                                borderTopStartRadius={"0.375rem"}
                                borderBottomStartRadius={"0.375rem"}
                                borderTopEndRadius={"0.375rem"}
                                borderBottomEndRadius={"0.375rem"}
                                p={2}
                                position="absolute"
                                bottom="10px"
                                right="10px"
                              ><small><em>starting at</em></small><br />${destination.bd_lowest_rate}</Link>
                            )}
                          </Box>
                          <Box position="relative" p={2}>
                            <Link href={`/d/${destination.slug}`}>
                              <Heading
                                color={headingColor}
                                fontSize={'lg'}
                                fontFamily={'roboto'}
                                fontWeight={600}
                                pt={2}
                                lineHeight="115%"
                                pb={2}>
                                {destination.name}
                              </Heading>
                            </Link>
                          </Box>
                        </Box>
                      </Box>
                    ))
                    }
                  </Masonry>
                )}

              </Box>
              <Box>
                <Box sx={{ position: ['-webkit-sticky', 'sticky'], top: '0px', }}>
                  <MapContainer center={position} zoom={12} scrollWheelZoom={false} style={{ minHeight: L.Browser.mobile ? '50vh' : '100vh' }} tap={!L.Browser.mobile} dragging={!L.Browser.mobile}>
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <LocationMarkers />
                  </MapContainer>
                </Box>
              </Box>
            </SimpleGrid>

          </Box>
        </>
      ) : null}
    </PageSlideFade>
  );
};

export default Experience;
